import { z } from 'zod/v4';

/**
 * File-manager pick shapes, mirroring the host app's `SelectedFileExtended`
 * (`common/types/file.type.ts` — `SelectedFileInterface & ImageOverlaySettings &
 * { …extras }`). Widget data schemas that hold a picked file (e.g. video's
 * `videoFile` / `customPreviewFile`) reference {@link selectedFileSchemaLegacy} so the
 * host gets `file.url` typed without casting back to `SelectedFileExtended`.
 *
 * Lenient by design: only `url` is required (the field consumers actually read);
 * everything else is optional.
 *
 * Plain (non-`passthrough`) objects: the inferred types carry no index signature,
 * which keeps the host's `SelectedFileExtended` / `SelectedFileInterface` (both
 * `interface`-based, so they have no implicit index signature) directly assignable
 * to this schema — writing a picked file into a form needs no cast. The trade-off
 * is that parsing strips keys not modelled below; the field list mirrors the host
 * type in full so a well-formed pick round-trips intact.
 *
 * The `ImageOverlaySettings` fields the host types with enums (`EConditionType`,
 * `EOverlayType`, `ESize`, `EFontStyle`, `E{Vertical,Horizontal}Position`,
 * `EMaskShapeType`) are modelled here as plain strings rather than mirrored
 * `z.enum`s — the package round-trips this presentation metadata without
 * validating it, and plain strings keep the host's enum-typed values assignable.
 */

/**
 * Mirrors `@sendpulse/file-manager`'s `SelectedFileInterface` (a directory item +
 * `url`/`defaultCdnUrl`). Only `url` is required; the rest are optional so partial
 * legacy picks still validate.
 *
 * Every additive field is `.nullish()` (accepts `undefined` **and** `null`):
 * real legacy picks on disk store `null` — not `undefined` — for fields the user
 * never set, so a lenient persistence schema must tolerate it. `date` is widened
 * to `string | number` because legacy records persist it as an epoch number while
 * newer picks use an ISO string.
 */
export const selectedFileInterfaceSchemaLegacy = z.object({
    url: z.string(),
    defaultCdnUrl: z.string().nullish(),
    path: z.string().nullish(),
    name: z.string().nullish(),
    size: z.number().nullish(),
    filesCount: z.number().nullish(),
    isFolder: z.boolean().nullish(),
    date: z.union([z.string(), z.number()]).nullish(),
    extension: z.string().nullish(),
    thumb: z.string().nullish(),
    stream: z.string().nullish(),
    directoriesCount: z.number().nullish(),
});

/**
 * Mirrors `SelectedFileExtended` — `SelectedFileInterface` plus the
 * `ImageOverlaySettings` group and the host's extra layout/CDN metadata. All
 * additive fields are `.nullish()` (accept `undefined` and `null`): legacy picks
 * persist `null` for unset overlay/layout metadata, so the persistence schema
 * must round-trip it rather than reject. See {@link selectedFileInterfaceSchemaLegacy}.
 */
export const selectedFileSchemaLegacy = z.object({
    ...selectedFileInterfaceSchemaLegacy.shape,

    // ImageOverlaySettings (enum-typed on the host; see file header)
    isImageEffect: z.boolean().nullish(),
    condition: z.string().nullish(),
    overlay: z.string().nullish(),
    description: z.string().nullish(),
    textSize: z.string().nullish(),
    fontSize: z.number().nullish(),
    textStyle: z.array(z.string()).nullish(),
    textColor: z.string().nullish(),
    textBackground: z.string().nullish(),
    paddingTop: z.number().nullish(),
    paddingBottom: z.number().nullish(),
    paddingLeft: z.number().nullish(),
    paddingRight: z.number().nullish(),
    textPosition: z.string().nullish(),
    textJustify: z.string().nullish(),
    maskShape: z.string().nullish(),
    overlayImage: selectedFileInterfaceSchemaLegacy.nullish(),

    // SelectedFileExtended extras
    alt: z.string().nullish(),
    link: z.string().nullish(),
    imageRound: z.number().nullish(),
    isActive: z.boolean().nullish(),
    imageWidth: z.number().nullish(),
    imageHeight: z.number().nullish(),
    resizedHeight: z.number().nullish(),
    resizedWidth: z.number().nullish(),
    backgroundPositionX: z.number().nullish(),
    backgroundPositionY: z.number().nullish(),
    sendpulseCdn: z.string().nullish(),
    hasResize: z.boolean().nullish(),
    hasSourceResize: z.boolean().nullish(),
    isDefault: z.boolean().nullish(),
    isTargetBlank: z.boolean().nullish(),
    srcSets: z.any().nullish(),
});

/** Inferred type for a picked file — the package's view of `SelectedFileExtended`. */
export type SelectedFileLegacy = z.infer<typeof selectedFileSchemaLegacy>;
